<template>
  <div
    class="flex flex-col items-center mx-4 mt-3 px-2"
    id="small-receipt"
    :class="{
      'dir-ltr': $ctx.receiptLanguage == 'en',
      'dir-rtl': $ctx.receiptLanguage == 'ar'
    }"
  >
    <div class="flex flex-col items-center w-full" style="height: 100vh;">
      <!-- header info -->
      <div id="small-receipt-header" class="flex flex-col w-full">
        <div class="flex mt-6 justify-center w-full">
          <img
            :src="settingsStore.getValue('receipt_logo') ? settingsStore.storagePath + '/' + settingsStore.getValue('receipt_logo'): '/images/logo.svg'"
            :width="settingsStore.getValue('receipt_logo_width')"
            alt=""
          />
        </div>
      </div>

      <div id="small-receipt-body" class="w-full">
        <!-- title -->
        <div class="flex flex-col w-full mt-2 items-center">
          <h2 class="bg-gray-200 rounded py-1 px-2 font-bold mt-3 capitalize">
            <p>{{ receiptTranslate("Vendor Bill Receipt", "receipt.vendorBillReceipt") }}</p>
          </h2>
        </div>

        <div class="flex flex-col px-5 items-center">
          <div>
            <svg :id="`barcode-${vendorBill?.externalID}`" class="mt-3"></svg>
            <div class="flex items-center justify-center">
              <span class="font-bold mx-5" style="font-size: 20px;">
                {{ vendorBill?.externalID?.toString().slice(0, 5) }}-{{ vendorBill?.externalID?.toString().slice(5) }}
              </span>
            </div>
          </div>
        </div>

        <div class="mx-5 mt-5 pb-2 text-sm self-end border-b border-black">
          <p style="font-size: 12px;">{{ settingsStore.getValue('company_address') }}</p>
          <p :class="{'ltr-numbers': settingsStore.getValue('receipt_language') == 'ar'}">
            {{ settingsStore.getValue('company_phone1') + (settingsStore.getValue('company_phone2')? ` - ${settingsStore.getValue('company_phone2')}`: '') }}
          </p>
          <p>{{ settingsStore.getValue('company_email') }}</p>
          <p>{{ settingsStore.getValue('company_website') }}</p>
        </div>

        <div class="flex w-full px-5 mt-4">
          <div>
            <table class="w-full text-sm">
              <tr>
                <td><p>{{ receiptTranslate("Vendor", "receipt.vendor") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.vendor?.name || vendorBill?.vendorName }}</p></td>
              </tr>
              <tr v-if="vendorBill?.vendorBillNumber">
                <td><p>{{ receiptTranslate("Bill Number", "receipt.billNumber") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.vendorBillNumber }}</p></td>
              </tr>
              <tr v-if="vendorBill?.billDate">
                <td><p>{{ receiptTranslate("Bill Date", "receipt.billDate") }}</p></td>
                <td><p class="font-bold mx-5">{{ parseFormat(vendorBill?.billDate) }}</p></td>
              </tr>
              <tr v-if="vendorBill?.billDueDate">
                <td><p>{{ receiptTranslate("Due Date", "receipt.dueDate") }}</p></td>
                <td><p class="font-bold mx-5">{{ parseFormat(vendorBill?.billDueDate) }}</p></td>
              </tr>
              <tr v-if="vendorBill?.whatsFor">
                <td><p>{{ receiptTranslate("Purpose", "receipt.purpose") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.whatsFor }}</p></td>
              </tr>
              <tr v-if="vendorBill?.debitedAccount">
                <td><p>{{ receiptTranslate("Debited Account", "receipt.debitedAccount") }}</p></td>
                <td><p class="font-bold mx-5">{{ $ctx.locale === 'ar' ? vendorBill?.debitedAccount?.arName : vendorBill?.debitedAccount?.enName }}</p></td>
              </tr>
              <tr v-if="vendorBill?.user">
                <td><p>{{ receiptTranslate("Created By", "receipt.createdBy") }}</p></td>
                <td><p class="font-bold mx-5">{{ vendorBill?.user?.fullName }}</p></td>
              </tr>
              <tr v-if="vendorBill?.createdAt">
                <td><p>{{ receiptTranslate("Date & Time", "receipt.dateAndTime") }}</p></td>
                <td><p class="font-bold mx-5" dir="ltr">{{ parseFormat(vendorBill?.createdAt) }}</p></td>
              </tr>
            </table>
          </div>
        </div>

        <!-- financial summary -->
        <div class="text-sm flex flex-col w-full px-5 mt-4">
          <div class="w-full">
            <div class="w-full border-t border-gray-300 pt-2">
              <table class="payment-table w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                <tr>
                  <td>{{ receiptTranslate("Total Amount", "receipt.totalAmount") }}</td>
                  <td><p class="text-right">{{ formatDinero(vendorBill?.totalDueDinero) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Total Paid", "receipt.totalPaid") }}</td>
                  <td><p class="text-right">{{ formatDinero(vendorBill?.totalPaidDinero) }}</p></td>
                </tr>
                <tr>
                  <td>{{ receiptTranslate("Balance", "receipt.balance") }}</td>
                  <td><p class="text-right font-bold">{{ formatDinero(vendorBill?.totalBalanceDinero) }}</p></td>
                </tr>
              </table>
            </div>
          </div>
        </div>

        <!-- notes -->
        <div v-if="vendorBill?.notes" class="px-5 mt-4">
          <p class="font-bold">{{ receiptTranslate("Notes", "receipt.notes") }}:</p>
          <p class="text-sm">{{ vendorBill?.notes }}</p>
        </div>

        <!-- transactions list -->
        <div v-if="transactions.length > 0" class="mt-4 px-5">
          <div class="flex flex-col w-full">
            <h3 class="bg-gray-100 rounded py-1 px-2 font-bold text-center">
              {{ receiptTranslate("Payment Transactions", "receipt.paymentTransactions") }}
            </h3>
          </div>

          <div class="text-sm flex flex-col w-full mt-2">
            <div class="w-full" v-for="transaction in transactions" :key="transaction.id">
              <div class="border-t border-gray-300 pt-2 mt-2">
                <table class="payment-table w-full" :class="{ 'ar-receipt': $ctx.receiptLanguage == 'ar' }">
                  <tr>
                    <td colspan="2">
                      <p class="font-bold">{{ receiptTranslate("Payment ID", "receipt.paymentID") }}: {{ transaction.externalID }}</p>
                    </td>
                  </tr>
                  <tr>
                    <td>{{ receiptTranslate("Date", "receipt.date") }}</td>
                    <td><p class="text-right">{{ parseFormat(transaction.createdAt) }}</p></td>
                  </tr>
                  <tr>
                    <td>{{ receiptTranslate("Amount", "receipt.amount") }}</td>
                    <td><p class="text-right">{{ formatDinero(transaction.amountDinero) }}</p></td>
                  </tr>
                  <tr v-if="transaction.description">
                    <td>{{ receiptTranslate("Notes", "receipt.notes") }}</td>
                    <td><p class="text-right">{{ transaction.description }}</p></td>
                  </tr>
                  <tr v-if="transaction.user && settingsStore.getValue('hide_cashier_name_in_receipt') != 'true'">
                    <td>{{ receiptTranslate("Cashier", "receipt.cashier") }}</td>
                    <td><p class="text-right">{{ transaction.user.fullName }}</p></td>
                  </tr>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        id="small-receipt-footer"
        v-if="settingsStore.getValue('show_receipts_thecky_footer') == 'true'"
        class="flex justify-center self-center w-full mt-auto"
      >
        <div class="flex items-center justify-between py-2 px-4 mt-2 mb-4" style="bottom: 0px; width: 95%; border-top: 2px solid #BDC7D6">
          <p><img src="@/assets/images/thecky-qr.png" style="height: 25px;"/></p>
          <p><img src="@/assets/images/footer.svg" style="height: 25px;"/></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import { parseFormat } from "@/utils/date";
import { useI18n } from "vue-i18n";
import { formatDinero } from "@/utils/money";
import { Transaction } from "@/models/accounting/Transaction";
import JsBarcode from "jsbarcode";
import { useSettingsStore } from "@/stores/settingsStore";
import VendorBill, { vendorBillI18nMessages } from "@/models/vendor/VendorBill";

export default defineComponent({
  components: {},

  setup() {
    const { t } = useI18n({
      messages: {
        en: {
          ...vendorBillI18nMessages.en,
          receipt: {
            vendorBillReceipt: 'Vendor Bill Receipt',
            vendor: 'Vendor',
            billNumber: 'Bill Number',
            billDate: 'Bill Date',
            dueDate: 'Due Date',
            purpose: 'Purpose',
            debitedAccount: 'Debited Account',
            createdBy: 'Created By',
            dateAndTime: 'Date & Time',
            notes: 'Notes',
            totalAmount: 'Total Amount',
            totalPaid: 'Total Paid',
            balance: 'Balance',
            paymentTransactions: 'Payment Transactions',
            paymentID: 'Payment ID',
            date: 'Date',
            amount: 'Amount',
            cashier: 'Cashier'
          }
        },
        ar: {
          ...vendorBillI18nMessages.ar,
          receipt: {
            vendorBillReceipt: 'فاتورة مورد',
            vendor: 'المورد',
            billNumber: 'رقم الفاتورة',
            billDate: 'تاريخ الفاتورة',
            dueDate: 'تاريخ الاستحقاق',
            purpose: 'الغرض',
            debitedAccount: 'حساب المدين',
            createdBy: 'انشئ من قبل',
            dateAndTime: 'التاريخ والوقت',
            notes: 'ملاحظات',
            totalAmount: 'المبلغ الإجمالي',
            totalPaid: 'المبلغ المدفوع',
            balance: 'الرصيد',
            paymentTransactions: 'حركات الدفع',
            paymentID: 'رقم الدفعة',
            date: 'التاريخ',
            amount: 'المبلغ',
            cashier: 'الكاشير'
          }
        }
      }
    });

    const settingsStore = useSettingsStore();
    return { t, settingsStore };
  },

  props: {
    vendorBill: {
      type: Object as PropType<VendorBill>,
      required: true
    },
    transactions: {
      type: Array as PropType<Array<Transaction>>,
      required: true
    }
  },

  mounted() {
    setTimeout(() => {
      if (this.vendorBill?.externalID) {
        JsBarcode(
          `#barcode-${this.vendorBill.externalID}`,
          String(this.vendorBill.externalID),
          {
            height: 50,
            displayValue: false
          }
        );
      }
    }, 100);
  },

  created() {
    this.addPrintCSS();
  },

  beforeUnmount() {
    this.removePrintCSS();
  },

  methods: {
    parseFormat,
    formatDinero,
    addPrintCSS() {
      const style = document.createElement("style");
      style.setAttribute("id", "print-css-small");
      style.textContent = `
        @media print {
          @page {
            size: 80mm auto;
            margin: 0;
          }
        }`;
      document.head.appendChild(style);
    },
    removePrintCSS() {
      const style = document.querySelector("#print-css-small");
      if (style && style.parentNode) {
        style.parentNode.removeChild(style);
      }
    },
    receiptTranslate(value: string, translateKey: string): string {
      return this.$ctx.receiptLanguage == "en" ? value : this.t(translateKey, this.$ctx.receiptLanguage);
    }
  }
});
</script>

<style>
.thecky-site {
  color: #000;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: 3px;
}

#small-receipt {
  width: 80mm;
  overflow: scroll;
  background-color: #fff;
}

.payment-table {
  width: 100%;
  border-collapse: collapse;
}

.payment-table td {
  padding: 4px;
}

@media print {
  body * {
    visibility: hidden;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }

  #small-receipt,
  #small-receipt * {
    visibility: visible;
  }

  #small-receipt {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: auto;
    overflow: visible;
    margin: 0;
  }
}
</style>
